import React from 'react';

export const Skeleton = ({ className = '', style }) => (
  <div className={`skeleton ${className}`} style={style} aria-hidden="true" />
);

export const SkeletonCircle = ({ className = '', size = 40 }) => (
  <div
    className={`skeleton skeleton-circle ${className}`}
    style={{ width: size, height: size }}
    aria-hidden="true"
  />
);

/** Skeleton for page-level loading — mirrors real page layouts */
export const PageSkeleton = ({ variant = 'cards' }) => {
  if (variant === 'dashboard') {
    return (
      <div
        className="h-full min-h-0 flex flex-col bg-neutral-100 dark:bg-neutral-950 overflow-hidden fade-in"
        aria-busy="true"
        aria-label="Laden"
      >
        <header className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 px-2 sm:px-4 py-2 sm:py-3">
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5 sm:gap-2">
            {[1, 2, 3, 4, 5].map((i) => (
              <div
                key={i}
                className="rounded-lg border border-neutral-200 dark:border-neutral-800 bg-neutral-50 dark:bg-neutral-950/60 px-2.5 py-2 sm:px-3 sm:py-2.5 space-y-2 min-h-[3.25rem]"
              >
                <Skeleton className="h-2.5 w-14" />
                <Skeleton className="h-5 w-10" />
              </div>
            ))}
          </div>
          <div className="mt-2 sm:mt-3 space-y-1.5">
            <div className="flex justify-between">
              <Skeleton className="h-2.5 w-20 sm:w-28" />
              <Skeleton className="h-2.5 w-16" />
            </div>
            <Skeleton className="h-1.5 sm:h-2 w-full rounded-full" />
          </div>
        </header>

        <div className="flex-1 min-h-0 flex flex-col gap-1.5 sm:gap-2 p-1 sm:p-2 overflow-hidden">
          {/* Kalender (embedded) — Layout wie CalendarPage */}
          <div className="flex-1 min-h-0 overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex flex-col">
            <div className="flex-1 min-h-0 px-2.5 sm:px-3 py-2.5 flex flex-col">
              <div className="flex-1 min-h-0 grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_280px] gap-3">
                <div className="min-h-0 flex flex-col overflow-hidden">
                  <div className="flex items-center justify-between gap-3 mb-2 flex-shrink-0">
                    <div className="flex items-center gap-2 min-w-0">
                      <Skeleton className="h-8 w-8 rounded-lg" />
                      <Skeleton className="h-8 w-8 rounded-lg" />
                      <Skeleton className="h-5 w-32" />
                    </div>
                    <Skeleton className="h-8 w-16 rounded-lg flex-shrink-0" />
                  </div>
                  <div className="grid grid-cols-7 gap-1 mb-1 flex-shrink-0">
                    {[1, 2, 3, 4, 5, 6, 7].map((i) => (
                      <Skeleton key={i} className="h-3 w-6 mx-auto" />
                    ))}
                  </div>
                  <div className="flex-1 min-h-0 grid grid-cols-7 grid-rows-6 gap-1">
                    {Array.from({ length: 42 }).map((_, i) => (
                      <div
                        key={i}
                        className="min-h-0 rounded-md border border-neutral-100 dark:border-neutral-800 p-1 flex flex-col gap-0.5 overflow-hidden"
                      >
                        <Skeleton className="h-2.5 w-4 mx-auto" />
                        {i % 5 === 0 ? (
                          <Skeleton className="h-2 w-2 rounded-full mx-auto hidden sm:block" style={{ opacity: 0.7 }} />
                        ) : null}
                      </div>
                    ))}
                  </div>
                </div>

                <aside className="hidden xl:flex min-h-0 flex-col rounded-xl border border-neutral-200 dark:border-neutral-800 overflow-hidden">
                  <div className="px-3 py-3 border-b border-neutral-100 dark:border-neutral-800 space-y-2">
                    <Skeleton className="h-2.5 w-20" />
                    <Skeleton className="h-5 w-40" />
                    <Skeleton className="h-8 w-full rounded-lg" />
                  </div>
                </aside>
              </div>
            </div>
          </div>

          <div className="flex-shrink-0 h-[6.5rem] sm:h-28 md:h-32">
            <div className="h-full rounded-lg border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden flex flex-col">
              <div className="px-2.5 py-1.5 border-b border-neutral-200 dark:border-neutral-800 flex justify-between">
                <Skeleton className="h-2.5 w-24" />
                <Skeleton className="h-2.5 w-6" />
              </div>
              <div className="flex-1 divide-y divide-neutral-100 dark:divide-neutral-800">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="px-3 py-2 flex items-center gap-2">
                    <Skeleton className="h-3 flex-1" />
                    <Skeleton className="h-3 w-12 hidden sm:block" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'campaigns') {
    return (
      <div className="space-y-3 sm:space-y-4 fade-in" aria-busy="true" aria-label="Laden">
        <header className="space-y-1.5">
          <Skeleton className="h-7 w-36 sm:h-8 sm:w-40" />
          <Skeleton className="hidden sm:block h-4 w-52" />
        </header>

        <section className="border border-stone-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 rounded-lg overflow-hidden">
          <div className="md:hidden">
            <div className="grid grid-cols-4 divide-x divide-stone-200 dark:divide-neutral-800">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="px-2 py-2 space-y-1 min-w-0">
                  <Skeleton className="h-2.5 w-10" />
                  <Skeleton className="h-4 w-7" />
                </div>
              ))}
            </div>
            <div className="grid grid-cols-3 divide-x border-t border-stone-200 dark:border-neutral-800 divide-stone-200 dark:divide-neutral-800">
              {[5, 6, 7].map((i) => (
                <div key={i} className="px-2 py-2 space-y-1 min-w-0">
                  <Skeleton className="h-2.5 w-10" />
                  <Skeleton className="h-4 w-7" />
                </div>
              ))}
            </div>
          </div>
          <div className="hidden md:flex divide-x divide-stone-200 dark:divide-neutral-800">
            {[1, 2, 3, 4, 5, 6, 7].map((i) => (
              <div key={i} className="flex-1 min-w-0 px-2 py-2.5 flex items-start gap-1.5">
                <Skeleton className="hidden lg:block h-6 w-6 rounded-md flex-shrink-0" />
                <div className="space-y-1 min-w-0 flex-1">
                  <Skeleton className="h-3 w-12" />
                  <Skeleton className="h-5 w-9" />
                  <Skeleton className="h-2 w-14" />
                </div>
              </div>
            ))}
          </div>
        </section>

        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Skeleton className="h-9 sm:h-10 flex-1 sm:max-w-md rounded-xl" />
          <div className="grid grid-cols-[1fr_auto] gap-2 sm:contents">
            <Skeleton className="h-9 sm:h-10 rounded-xl" />
            <Skeleton className="h-9 w-16 sm:h-10 sm:w-40 rounded-xl" />
          </div>
        </div>

        <section className="border border-stone-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 rounded-lg overflow-hidden">
          <div className="px-3 sm:px-5 py-2 sm:py-3 border-b border-stone-200 dark:border-neutral-800 flex justify-between">
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-3 w-6" />
          </div>
          <div className="divide-y divide-stone-100 dark:divide-neutral-800">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="px-3 py-2 sm:px-4 sm:py-2.5 space-y-1.5">
                <div className="flex items-center justify-between gap-3">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <Skeleton className="h-3.5 w-[55%]" />
                    <Skeleton className="h-2.5 w-[40%]" />
                  </div>
                  <Skeleton className="h-6 w-14 rounded" />
                </div>
                <div className="flex items-center gap-2 pl-5 sm:pl-6">
                  <Skeleton className="h-2.5 w-14" />
                  <Skeleton className="h-1 flex-1 max-w-xs rounded-full" />
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    );
  }

  if (variant === 'finder') {
    return (
      <div className="space-y-5 fade-in" aria-busy="true" aria-label="Laden">
        <div className="grid grid-cols-1 lg:grid-cols-[22rem_1fr] gap-5">
          {/* Linke Spalte: Formular + Verlauf */}
          <div className="space-y-4">
            <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 space-y-3">
              <div className="grid grid-cols-2 gap-1.5 p-1 bg-neutral-100 dark:bg-neutral-800 rounded-xl">
                <Skeleton className="h-8 rounded-lg" />
                <Skeleton className="h-8 rounded-lg" />
              </div>
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-20" />
                <div className="flex gap-2">
                  <Skeleton className="h-10 flex-1 rounded-xl" />
                  <Skeleton className="h-10 w-10 rounded-lg flex-shrink-0" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Skeleton className="h-3 w-32" />
                <div className="flex items-center gap-3">
                  <Skeleton className="h-2 flex-1 rounded-full" />
                  <Skeleton className="h-8 w-16 rounded-lg flex-shrink-0" />
                </div>
              </div>
              <div className="space-y-2">
                <Skeleton className="h-3 w-24" />
                <Skeleton className="h-2.5 w-20" />
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Skeleton className="h-3.5 w-3.5 rounded" />
                    <Skeleton className="h-3 w-28" />
                  </div>
                ))}
                <Skeleton className="h-2.5 w-24 mt-1" />
                {[1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Skeleton className="h-3.5 w-3.5 rounded" />
                    <Skeleton className="h-3 w-24" />
                  </div>
                ))}
              </div>
              <Skeleton className="h-10 w-full rounded-xl" />
            </div>

            <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden">
              <div className="px-4 py-3 border-b border-neutral-100 dark:border-neutral-800 flex items-center justify-between">
                <Skeleton className="h-4 w-16" />
                <Skeleton className="h-3 w-20" />
              </div>
              <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="px-4 py-2.5 flex items-center gap-2">
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <Skeleton className="h-3.5 w-[70%]" />
                      <Skeleton className="h-2.5 w-[45%]" />
                    </div>
                    <Skeleton className="h-5 w-14 rounded-full flex-shrink-0" />
                    <Skeleton className="h-3.5 w-3.5 rounded flex-shrink-0" />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Rechte Spalte: Tabs + Ergebnis-Karten */}
          <div className="min-w-0 space-y-4">
            <div className="grid grid-cols-3 gap-1 p-1 bg-neutral-100 dark:bg-neutral-800 rounded-xl">
              <Skeleton className="h-8 rounded-lg" />
              <Skeleton className="h-8 rounded-lg" />
              <Skeleton className="h-8 rounded-lg" />
            </div>

            <div className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1 space-y-2">
                  <Skeleton className="h-5 w-[55%]" />
                  <Skeleton className="h-3 w-[35%]" />
                </div>
                <Skeleton className="h-6 w-16 rounded-full flex-shrink-0" />
              </div>
              <Skeleton className="h-2 w-full rounded-full" />
              <div className="flex flex-wrap gap-1.5">
                <Skeleton className="h-6 w-24 rounded-lg" />
                <Skeleton className="h-6 w-28 rounded-lg" />
                <Skeleton className="h-6 w-20 rounded-lg" />
              </div>
            </div>

            <div className="space-y-3">
              {[1, 2, 3, 4].map((i) => (
                <div
                  key={i}
                  className="rounded-2xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-4 space-y-3"
                >
                  <div className="flex items-start gap-3">
                    <Skeleton className="h-10 w-10 rounded-xl flex-shrink-0" />
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <Skeleton className="h-4 w-[55%]" />
                      <Skeleton className="h-3 w-[40%]" />
                    </div>
                    <Skeleton className="h-5 w-10 rounded-full flex-shrink-0" />
                  </div>
                  <Skeleton className="h-3 w-full" />
                  <Skeleton className="h-3 w-[80%]" />
                  <div className="flex flex-wrap gap-2 pt-1">
                    <Skeleton className="h-8 w-28 rounded-lg" />
                    <Skeleton className="h-8 w-24 rounded-lg" />
                    <Skeleton className="h-8 w-8 rounded-lg" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'contacts' || variant === 'cards') {
    return (
      <div className="space-y-6 fade-in" aria-busy="true" aria-label="Laden">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div className="space-y-2">
            <Skeleton className="h-8 w-36" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="flex gap-2 flex-wrap">
            <Skeleton className="h-10 w-28 rounded-xl" />
            <Skeleton className="h-10 w-28 rounded-xl" />
            <Skeleton className="h-10 w-24 rounded-xl" />
            <Skeleton className="h-10 w-36 rounded-xl" />
          </div>
        </div>

        <div className="flex flex-col sm:flex-row gap-3 flex-wrap">
          <Skeleton className="h-10 flex-1 min-w-[12rem] max-w-md rounded-xl" />
          <Skeleton className="h-10 w-44 rounded-xl" />
          <Skeleton className="h-10 w-44 rounded-xl" />
          <Skeleton className="h-10 w-44 rounded-xl" />
          <Skeleton className="h-10 w-20 rounded-xl" />
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-2">
          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
            <div
              key={i}
              className="rounded-xl border border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 p-3 space-y-2"
            >
              <div className="flex items-start gap-2">
                <Skeleton className="h-3.5 w-3.5 rounded mt-1 flex-shrink-0" />
                <Skeleton className="h-8 w-8 rounded-lg flex-shrink-0" />
                <div className="min-w-0 flex-1 space-y-1.5">
                  <Skeleton className="h-3.5 w-[70%]" />
                  <Skeleton className="h-2.5 w-[45%]" />
                </div>
              </div>
              <Skeleton className="h-3 w-[85%]" />
              <Skeleton className="h-2.5 w-[55%]" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (variant === 'templates') {
    return (
      <div className="space-y-3 sm:space-y-6 fade-in" aria-busy="true" aria-label="Laden">
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1.5">
            <Skeleton className="h-7 w-32 sm:h-9 sm:w-40" />
            <Skeleton className="hidden sm:block h-4 w-64" />
          </div>
          <Skeleton className="h-9 w-14 sm:h-10 sm:w-36 rounded-xl" />
        </div>

        <div className="grid grid-cols-3 gap-0.5 p-1 rounded-xl border border-neutral-200 dark:border-neutral-800 sm:flex sm:gap-1 sm:rounded-none sm:border-0 sm:border-b sm:p-0">
          {[1, 2, 3].map((i) => (
            <div key={i} className="px-2 py-2.5 sm:px-4 sm:py-3 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-2">
              <Skeleton className="h-3.5 w-3.5 rounded" />
              <Skeleton className="h-3 w-12 sm:h-4 sm:w-16" />
            </div>
          ))}
        </div>

        <div className="flex flex-col gap-2 sm:flex-row sm:gap-3">
          <Skeleton className="h-9 sm:h-10 w-full sm:max-w-md rounded-xl" />
          <Skeleton className="h-9 sm:h-8 w-full sm:w-48 rounded-xl" />
        </div>

        {[1, 2].map((section) => (
          <div
            key={section}
            className="rounded-2xl border border-neutral-200/80 dark:border-neutral-800 bg-white dark:bg-neutral-900 overflow-hidden"
          >
            <div className="px-3 sm:px-5 py-2.5 sm:py-3.5 border-b border-neutral-100 dark:border-neutral-800 space-y-1.5">
              <Skeleton className="h-4 w-28" />
              <Skeleton className="hidden sm:block h-3 w-48" />
            </div>
            <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="px-3 sm:px-5 py-3 sm:py-3.5 flex items-start gap-3">
                  <SkeletonCircle size={32} className="flex-shrink-0" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <Skeleton className="h-3.5 w-[40%]" />
                      <Skeleton className="h-5 w-16 rounded-full" />
                    </div>
                    <Skeleton className="h-3 w-[55%]" />
                    <Skeleton className="h-2.5 w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'inbox') {
    return (
      <div
        className="h-full min-h-0 flex flex-col bg-white dark:bg-neutral-950 fade-in"
        aria-busy="true"
        aria-label="Laden"
      >
        <div className="flex-shrink-0 border-b border-neutral-200 dark:border-neutral-800 px-4 py-3 flex flex-wrap items-center gap-3 justify-between bg-white dark:bg-neutral-900">
          <Skeleton className="h-6 w-28" />
          <div className="flex items-center gap-2 flex-1 max-w-xl min-w-[12rem]">
            <Skeleton className="h-9 flex-1 rounded-xl" />
            <Skeleton className="h-9 w-28 rounded-xl flex-shrink-0" />
          </div>
          <div className="flex items-center gap-2">
            <Skeleton className="h-9 w-28 rounded-xl hidden sm:block" />
            <Skeleton className="h-9 w-32 rounded-xl" />
          </div>
        </div>

        <div className="flex-1 min-h-0 flex">
          <div className="w-full lg:w-[380px] xl:w-[420px] flex-shrink-0 border-r border-neutral-200 dark:border-neutral-800 bg-white dark:bg-neutral-900 flex flex-col">
            <div className="px-4 py-2.5 border-b border-neutral-100 dark:border-neutral-800 space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-4 w-4 rounded" />
                <Skeleton className="h-4 w-48" />
              </div>
            </div>
            <div className="flex-1 divide-y divide-neutral-100 dark:divide-neutral-800 overflow-hidden">
              {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                <div key={i} className="px-4 py-3 flex items-start gap-3">
                  <SkeletonCircle size={36} className="flex-shrink-0" />
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <Skeleton className="h-3.5 w-[45%]" />
                      <Skeleton className="h-2.5 w-10" />
                    </div>
                    <Skeleton className="h-3.5 w-[75%]" />
                    <Skeleton className="h-2.5 w-24" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="hidden lg:flex flex-1 flex-col bg-neutral-50/50 dark:bg-neutral-950">
            <div className="flex-1 flex flex-col items-center justify-center px-6">
              <Skeleton className="h-12 w-12 rounded-full mb-4" />
              <Skeleton className="h-4 w-40 mb-2" />
              <Skeleton className="h-3 w-56" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'settings') {
    return (
      <div className="space-y-6 fade-in" aria-busy="true" aria-label="Laden">
        <div className="space-y-2">
          <Skeleton className="h-9 w-48" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </div>

        {/* Design & Themes */}
        <div className="card overflow-hidden">
          <div className="card-header">
            <div className="flex items-center gap-2">
              <Skeleton className="h-5 w-5 rounded" />
              <div className="space-y-1.5">
                <Skeleton className="h-5 w-40" />
                <Skeleton className="h-3 w-56" />
              </div>
            </div>
          </div>
          <div className="card-body">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Skeleton key={i} className="h-20 w-full rounded-xl" />
              ))}
            </div>
          </div>
        </div>

        {/* Banner */}
        <div className="card overflow-hidden">
          <div className="card-header">
            <div className="flex justify-between gap-3">
              <div className="space-y-1.5">
                <div className="flex items-center gap-2">
                  <Skeleton className="h-5 w-5 rounded" />
                  <Skeleton className="h-5 w-40" />
                </div>
                <Skeleton className="h-3 w-64" />
              </div>
              <Skeleton className="h-5 w-16" />
            </div>
          </div>
          <div className="card-body space-y-5">
            <div className="space-y-2">
              <Skeleton className="h-3.5 w-36" />
              <div className="flex flex-wrap gap-2">
                {[1, 2, 3, 4].map((i) => (
                  <Skeleton key={i} className="h-7 w-24 rounded-lg" />
                ))}
              </div>
              <Skeleton className="h-36 w-full rounded-xl" />
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <Skeleton className="h-40 w-full rounded-xl" />
              <div className="space-y-3">
                <Skeleton className="h-10 w-full rounded-xl" />
                <Skeleton className="h-10 w-full rounded-xl" />
                <Skeleton className="h-10 w-full rounded-xl" />
              </div>
            </div>
            <Skeleton className="h-10 w-36 rounded-xl" />
          </div>
        </div>

        {/* Spam / Newsletter cards */}
        {[1, 2].map((i) => (
          <div key={i} className="card overflow-hidden">
            <div className="card-header">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-5 rounded" />
                <div className="space-y-1.5">
                  <Skeleton className="h-5 w-36" />
                  <Skeleton className="h-3 w-56" />
                </div>
              </div>
            </div>
            <div className="card-body space-y-3">
              <Skeleton className="h-16 w-full rounded-xl" />
              <Skeleton className="h-16 w-full rounded-xl" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (variant === 'followups') {
    return (
      <div className="space-y-6 fade-in" aria-busy="true" aria-label="Laden">
        <div className="space-y-2">
          <Skeleton className="h-9 w-64" />
          <Skeleton className="h-4 w-80 max-w-full" />
        </div>

        <div className="card">
          <div className="card-body flex items-start gap-3">
            <Skeleton className="h-5 w-5 rounded flex-shrink-0 mt-0.5" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-36" />
              <Skeleton className="h-3 w-full" />
              <Skeleton className="h-3 w-[90%]" />
            </div>
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="card-header">
            <Skeleton className="h-5 w-40" />
          </div>
          <div className="card-body space-y-4">
            {[1, 2, 3].map((i) => (
              <div
                key={i}
                className="flex items-center justify-between p-4 border border-neutral-200 dark:border-neutral-700 rounded-lg gap-4"
              >
                <div className="flex items-center gap-4 min-w-0">
                  <SkeletonCircle size={40} className="flex-shrink-0" />
                  <div className="space-y-1.5">
                    <Skeleton className="h-4 w-28" />
                    <Skeleton className="h-3 w-36" />
                  </div>
                </div>
                <div className="flex items-center gap-4 flex-shrink-0">
                  <Skeleton className="h-8 w-20 rounded-lg" />
                  <Skeleton className="h-4 w-16" />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-between items-center gap-3">
          <Skeleton className="h-4 w-48" />
          <div className="flex gap-2">
            <Skeleton className="h-10 w-28 rounded-xl" />
            <Skeleton className="h-10 w-28 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'detail') {
    return (
      <div className="space-y-6 fade-in" aria-busy="true" aria-label="Laden">
        <div className="flex items-center justify-between gap-4">
          <Skeleton className="h-5 w-40" />
          <div className="flex gap-2 flex-wrap justify-end">
            <Skeleton className="h-10 w-28 rounded-xl" />
            <Skeleton className="h-10 w-36 rounded-xl" />
            <Skeleton className="h-10 w-32 rounded-xl" />
            <Skeleton className="h-10 w-32 rounded-xl" />
          </div>
        </div>

        <div className="card overflow-hidden">
          <div className="card-header space-y-3">
            <div className="flex items-start justify-between gap-3">
              <Skeleton className="h-7 w-[60%]" />
              <Skeleton className="h-6 w-20 rounded-full" />
            </div>
            <div className="space-y-2.5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <Skeleton className="h-4 w-4 rounded flex-shrink-0" />
                  <Skeleton className="h-3 w-10" />
                  <Skeleton className="h-3 w-[40%]" />
                </div>
              ))}
            </div>
          </div>
          <div className="card-body space-y-2.5">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-[92%]" />
            <Skeleton className="h-3 w-[78%]" />
            <Skeleton className="h-3 w-[85%]" />
            <Skeleton className="h-3 w-[40%]" />
            <Skeleton className="h-3 w-full mt-4" />
            <Skeleton className="h-3 w-[88%]" />
            <Skeleton className="h-3 w-[70%]" />
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'form') {
    return <PageSkeleton variant="settings" />;
  }

  if (variant === 'stats') {
    return <PageSkeleton variant="campaigns" />;
  }

  if (variant === 'list') {
    return <PageSkeleton variant="contacts" />;
  }

  return <PageSkeleton variant="contacts" />;
};

/** Skeleton for modal content while async data loads */
export const ModalSkeleton = ({ lines = 5 }) => (
  <div className="space-y-4 py-2 fade-in" aria-busy="true" aria-label="Laden">
    <div className="flex items-center gap-3">
      <SkeletonCircle size={40} />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-[45%]" />
        <Skeleton className="h-3 w-[30%]" />
      </div>
    </div>
    <div className="space-y-2.5 pt-2">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className="h-2.5"
          style={{ width: `${92 - (i % 4) * 12}%` }}
        />
      ))}
    </div>
    <div className="space-y-3 pt-4">
      {[1, 2, 3].map((i) => (
        <div
          key={i}
          className="rounded-2xl border border-neutral-200 dark:border-neutral-800 p-4 space-y-2"
        >
          <div className="flex items-center gap-2">
            <Skeleton className="h-5 w-16 rounded-full" />
            <Skeleton className="h-3 w-28" />
          </div>
          <Skeleton className="h-3.5 w-[70%]" />
          <Skeleton className="h-2.5 w-full" />
          <Skeleton className="h-2.5 w-[85%]" />
        </div>
      ))}
    </div>
  </div>
);

export default PageSkeleton;
